<template>
  <div id="reportPage">
    <main>
      <article>
        <div class="title-wrapper mb-4">
          <h2 class="login-title">AGENTS & TEAMS</h2>
          <UserMenu :roles="roles" :user-name="userName" />
        </div>

        <div class="toolbar">
          <div class="toolbar-left">
            <input
              v-model="search"
              class="form-control search-input"
              placeholder="Search agents..."
              @input="debouncedLoadAgents"
            />
            <select v-model="teamFilter" class="form-select team-filter" @change="loadAgents">
              <option value="">All teams</option>
              <option value="unassigned">Unassigned</option>
              <option v-for="t in teams" :key="t.id" :value="String(t.id)">{{ t.name }}</option>
            </select>
          </div>
          <div class="toolbar-right">
            <button class="btn btn-outline-primary" :disabled="syncing" @click="syncAgents">
              {{ syncing ? "Syncing…" : "Sync from Ringba (7d)" }}
            </button>
            <button class="btn btn-primary" @click="openCreateTeam">+ Team</button>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger mt-2">{{ errorMessage }}</div>
        <div v-if="okMessage" class="alert alert-success mt-2">{{ okMessage }}</div>

        <div class="team-summary" v-if="teams.length">
          <div v-for="t in teams" :key="'sum-' + t.id" class="team-chip">
            <strong>{{ t.name }}</strong>
            <span>{{ t.agentCount }} agents</span>
            <button class="link-btn" @click="renameTeam(t)">Rename</button>
            <button class="link-btn danger" @click="removeTeam(t)">Delete</button>
          </div>
          <div class="team-chip">
            <strong>Unassigned</strong>
            <span>{{ unassignedCount }} agents</span>
          </div>
        </div>

        <div class="table-responsive mt-3">
          <table class="table table-bordered align-middle">
            <thead class="table-light">
              <tr>
                <th>Avatar ID</th>
                <th>User</th>
                <th>Full name</th>
                <th>Cluster</th>
                <th>Team</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in agents" :key="a.avatarId">
                <td>{{ a.avatarId }}</td>
                <td>{{ a.username || "—" }}</td>
                <td>{{ a.fullName || "—" }}</td>
                <td>{{ a.cluster || "—" }}</td>
                <td>
                  <select
                    class="form-select form-select-sm"
                    :value="a.teamId == null ? '' : String(a.teamId)"
                    @change="onTeamAssign(a, $event.target.value)"
                  >
                    <option value="">Unassigned</option>
                    <option v-for="t in teams" :key="t.id" :value="String(t.id)">{{ t.name }}</option>
                  </select>
                </td>
                <td>{{ formatSeen(a.lastSeenAt) }}</td>
              </tr>
              <tr v-if="!agents.length">
                <td colspan="6" class="text-center py-3">No agents yet. Sync from Ringba or open call logs.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </main>
  </div>
</template>

<script>
import UserMenu from "@/components/UserMenu.vue";

export default {
  components: { UserMenu },
  data() {
    return {
      userName: localStorage.getItem("username") || "User",
      roles: [],
      teams: [],
      agents: [],
      search: "",
      teamFilter: "",
      syncing: false,
      errorMessage: "",
      okMessage: "",
      searchTimer: null,
    };
  },
  computed: {
    unassignedCount() {
      return this.agents.filter((a) => !a.teamId).length;
    },
  },
  async mounted() {
    const token = localStorage.getItem("token");
    if (!token) {
      this.$router.push("/login");
      return;
    }
    await this.check_role();
    if (!this.roles.includes("admin") && !this.roles.includes("manager")) {
      this.$router.push("/calls");
      return;
    }
    document.title = "Agents & Teams";
    await this.loadTeams();
    await this.loadAgents();
  },
  methods: {
    authHeaders() {
      return { Authorization: `Bearer ${localStorage.getItem("token")}` };
    },
    async check_role() {
      const params = new URLSearchParams({ user: this.userName });
      const res = await fetch(`/api/role?${params.toString()}`, {
        headers: this.authHeaders(),
      });
      const data = await res.json();
      this.roles = data.roles || [];
    },
    formatSeen(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString("en-US", { timeZone: "America/New_York" });
    },
    debouncedLoadAgents() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadAgents(), 250);
    },
    async loadTeams() {
      const res = await fetch("/api/teams", { headers: this.authHeaders() });
      if (!res.ok) {
        this.errorMessage = "Failed to load teams";
        return;
      }
      const data = await res.json();
      this.teams = data.records || [];
    },
    async loadAgents() {
      this.errorMessage = "";
      const params = new URLSearchParams();
      if (this.search.trim()) params.set("search", this.search.trim());
      if (this.teamFilter === "unassigned") params.set("unassigned", "1");
      else if (this.teamFilter) params.set("team_id", this.teamFilter);
      const res = await fetch(`/api/agents?${params.toString()}`, {
        headers: this.authHeaders(),
      });
      if (!res.ok) {
        this.errorMessage = "Failed to load agents";
        return;
      }
      const data = await res.json();
      this.agents = data.records || [];
    },
    openCreateTeam() {
      const name = window.prompt("Team name");
      if (!name || !name.trim()) return;
      this.createTeam(name.trim());
    },
    async createTeam(name) {
      this.errorMessage = "";
      const res = await fetch("/api/teams", {
        method: "POST",
        headers: { ...this.authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        this.errorMessage = data.error || "Failed to create team";
        return;
      }
      this.okMessage = `Team "${name}" created`;
      await this.loadTeams();
    },
    async renameTeam(team) {
      const name = window.prompt("Rename team", team.name);
      if (!name || !name.trim() || name.trim() === team.name) return;
      const res = await fetch(`/api/teams/${team.id}`, {
        method: "PATCH",
        headers: { ...this.authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        this.errorMessage = data.error || "Failed to rename team";
        return;
      }
      await this.loadTeams();
      await this.loadAgents();
    },
    async removeTeam(team) {
      if (!window.confirm(`Delete team "${team.name}"? Agents become unassigned.`)) return;
      const res = await fetch(`/api/teams/${team.id}`, {
        method: "DELETE",
        headers: this.authHeaders(),
      });
      if (!res.ok) {
        this.errorMessage = "Failed to delete team";
        return;
      }
      await this.loadTeams();
      await this.loadAgents();
    },
    async onTeamAssign(agent, value) {
      const teamId = value === "" ? null : Number(value);
      const res = await fetch(`/api/agents/${encodeURIComponent(agent.avatarId)}`, {
        method: "PATCH",
        headers: { ...this.authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ teamId }),
      });
      if (!res.ok) {
        this.errorMessage = "Failed to assign team";
        await this.loadAgents();
        return;
      }
      await this.loadTeams();
      await this.loadAgents();
    },
    async syncAgents() {
      this.syncing = true;
      this.errorMessage = "";
      this.okMessage = "";
      try {
        const res = await fetch("/api/agents/sync", {
          method: "POST",
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.errorMessage = data.error || "Sync failed";
          return;
        }
        this.okMessage = `Synced from ${data.upsertedFromCalls || 0} calls. Agents total: ${data.agentsTotal}`;
        await this.loadAgents();
        await this.loadTeams();
      } finally {
        this.syncing = false;
      }
    },
  },
};
</script>

<style scoped>
#reportPage {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  padding: 20px 10px;
}

#reportPage main {
  display: flex;
  justify-content: center;
  width: 100%;
}

#reportPage article {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: none;
  position: relative;
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #4e54c8;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.search-input {
  min-width: 220px;
}

.team-filter {
  min-width: 160px;
}

.team-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.team-chip {
  display: flex;
  gap: 8px;
  align-items: center;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
}

.link-btn {
  border: none;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  padding: 0;
}

.link-btn.danger {
  color: #dc2626;
}
</style>
